import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

// Colors for shapes
const COLORS = {
  yellow: '#feff00',
  blue: '#0000fe',
  red: '#fe0000',
  green: '#00ff00',
  black: '#000000'
};

const COLOR_NAMES = Object.keys(COLORS);

// Letter pairs
const LETTER_PAIRS = [
  'CP', 'SX', 'VH', 'DW', 'HZ', 'RB', 'NY', 'LO', 'FU', 'LY',
  'WV', 'TH', 'CQ', 'JF', 'SZ', 'KH', 'GZ', 'MZ', 'RC', 'WT'
];

// Shape types with their variations
const SHAPES = [
  { type: 'square', filled: true, directions: [null] },
  { type: 'triangle', filled: true, directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'] },
  { type: 'circle', filled: true, directions: [null] },
  { type: 'semicircle', filled: true, directions: ['up', 'down'] },
  { type: 'ellipse-vertical', filled: false, directions: [null] },
  { type: 'ellipse-horizontal', filled: true, directions: [null] },
  { type: 'diamond', filled: false, directions: [null] },
  { type: 'x', filled: true, directions: [null] },
  { type: 'crescent', filled: true, directions: ['up', 'down'] },
  { type: 'cross', filled: true, directions: [null] },
  { type: 'arrow', filled: true, directions: ['up', 'down', 'left', 'right'] },
  { type: 'bar', filled: true, directions: ['horizontal', 'vertical'] },
  { type: 'arrowhead', filled: true, directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'] },
  { type: 'equal', filled: true, directions: ['horizontal', 'vertical'] },
  { type: 'no-entry', filled: true, directions: [null] }
];

// Generate a random 2-digit number (11-99)
const generateNumber = (usedNumbers) => {
  let num;
  do {
    num = Math.floor(Math.random() * 89) + 11;
  } while (usedNumbers.has(num));
  return num;
};

// Generate a random image (letter or shape)
const generateImage = (usedImages) => {
  const useShape = Math.random() > 0.5;
  
  if (useShape) {
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const color = COLOR_NAMES[Math.floor(Math.random() * COLOR_NAMES.length)];
    const direction = shape.directions[Math.floor(Math.random() * shape.directions.length)];
    const imageKey = `shape_${shape.type}_${color}_${direction}`;
    
    if (usedImages.has(imageKey)) {
      return generateImage(usedImages);
    }
    
    return {
      type: 'shape',
      shape: shape.type,
      color,
      direction,
      filled: shape.filled,
      key: imageKey
    };
  } else {
    const letter = LETTER_PAIRS[Math.floor(Math.random() * LETTER_PAIRS.length)];
    const imageKey = `letter_${letter}`;
    
    if (usedImages.has(imageKey)) {
      return generateImage(usedImages);
    }
    
    return {
      type: 'letter',
      letter,
      key: imageKey
    };
  }
};

// Generate pairs for a round
const generateRoundPairs = () => {
  const pairs = [];
  const usedNumbers = new Set();
  const usedImages = new Set();
  
  for (let i = 0; i < 20; i++) {
    const number = generateNumber(usedNumbers);
    usedNumbers.add(number);
    
    const image = generateImage(usedImages);
    usedImages.add(image.key);
    
    pairs.push({ id: i, number, image });
  }
  
  return pairs;
};

// Shape renderer component
const ShapeRenderer = ({ image, size = 80 }) => {
  if (image.type === 'letter') {
    return (
      <div 
        className="flex items-center justify-center font-bold text-gray-900 border border-gray-300 bg-white"
        style={{ width: size, height: size, fontSize: size * 0.5 }}
      >
        {image.letter}
      </div>
    );
  }
  
  const color = COLORS[image.color];
  const strokeWidth = 4;
  
  const renderShape = () => {
    switch (image.shape) {
      case 'square':
        return <rect x="10" y="10" width="80" height="80" fill={color} />;
      
      case 'triangle': {
        let points;
        switch (image.direction) {
          case 'top-left': points = "10,10 90,90 10,90"; break;
          case 'top-right': points = "90,10 90,90 10,90"; break;
          case 'bottom-left': points = "10,10 90,10 10,90"; break;
          case 'bottom-right': points = "10,10 90,10 90,90"; break;
          default: points = "50,10 90,90 10,90";
        }
        return <polygon points={points} fill={color} />;
      }
      
      case 'circle':
        return <circle cx="50" cy="50" r="40" fill={color} />;
      
      case 'semicircle': {
        const path = image.direction === 'up' 
          ? "M 10 60 A 40 40 0 0 1 90 60 Z"
          : "M 10 40 A 40 40 0 0 0 90 40 Z";
        return <path d={path} fill={color} />;
      }
      
      case 'ellipse-vertical':
        return <ellipse cx="50" cy="50" rx="25" ry="40" fill="none" stroke={color} strokeWidth={12} />;
      
      case 'ellipse-horizontal':
        return <ellipse cx="50" cy="55" rx="40" ry="25" fill={color} />;
      
      case 'diamond':
        return <polygon points="50,10 90,50 50,90 10,50" fill="none" stroke={color} strokeWidth={10} />;
      
      case 'x':
        return (
          <g fill={color}>
            <polygon points="25,15 50,40 75,15 85,25 60,50 85,75 75,85 50,60 25,85 15,75 40,50 15,25" />
          </g>
        );
      
      case 'crescent': {
        const path = image.direction === 'up'
          ? "M 15 70 A 40 40 0 1 1 85 70 A 30 30 0 1 0 15 70 Z"
          : "M 15 30 A 40 40 0 1 0 85 30 A 30 30 0 1 1 15 30 Z";
        return <path d={path} fill={color} />;
      }
      
      case 'cross':
        return (
          <g fill={color}>
            <rect x="35" y="10" width="30" height="80" />
            <rect x="10" y="35" width="80" height="30" />
          </g>
        );
      
      case 'arrow': {
        let points;
        switch (image.direction) {
          case 'up': points = "50,10 80,50 60,50 60,90 40,90 40,50 20,50"; break;
          case 'down': points = "50,90 80,50 60,50 60,10 40,10 40,50 20,50"; break;
          case 'left': points = "10,50 50,20 50,40 90,40 90,60 50,60 50,80"; break;
          case 'right': points = "90,50 50,20 50,40 10,40 10,60 50,60 50,80"; break;
          default: points = "50,10 80,50 60,50 60,90 40,90 40,50 20,50";
        }
        return <polygon points={points} fill={color} />;
      }
      
      case 'bar':
        return image.direction === 'horizontal'
          ? <rect x="10" y="40" width="80" height="20" fill={color} />
          : <rect x="40" y="10" width="20" height="80" fill={color} />;
      
      case 'arrowhead': {
        // Symmetrical right-angle triangle (isoceles) pointing to corner
        let points;
        switch (image.direction) {
          case 'top-left': points = "10,10 80,10 10,80"; break;
          case 'top-right': points = "20,10 90,10 90,80"; break;
          case 'bottom-left': points = "10,20 10,90 80,90"; break;
          case 'bottom-right': points = "90,20 90,90 20,90"; break;
          default: points = "10,10 80,10 10,80";
        }
        return <polygon points={points} fill={color} />;
      }
      
      case 'equal':
        return image.direction === 'horizontal' ? (
          <g fill={color}>
            <rect x="15" y="25" width="70" height="16" />
            <rect x="15" y="59" width="70" height="16" />
          </g>
        ) : (
          <g fill={color}>
            <rect x="25" y="15" width="16" height="70" />
            <rect x="59" y="15" width="16" height="70" />
          </g>
        );
      
      case 'no-entry':
        return (
          <g>
            <circle cx="50" cy="50" r="40" fill={color} />
            <rect x="5" y="42" width="90" height="16" fill="white" transform="rotate(-45 50 50)" />
          </g>
        );
      
      default:
        return <circle cx="50" cy="50" r="40" fill={color} />;
    }
  };
  
  return (
    <div className="flex items-center justify-center border border-gray-300" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size - 10} height={size - 10}>
        {renderShape()}
      </svg>
    </div>
  );
};

// Pair display component
const PairDisplay = ({ pair, size = 140 }) => (
  <div className="flex items-center gap-4">
    <ShapeRenderer image={pair.image} size={size} />
    <div 
      className="flex items-center justify-center font-bold text-gray-900"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {pair.number}
    </div>
  </div>
);

// Numpad component
const Numpad = ({ onNumber, onDelete, onSubmit, disabled }) => {
  const buttons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['0', 'DEL', 'OK']
  ];
  
  return (
    <div className="grid grid-cols-3 gap-2">
      {buttons.flat().map((btn) => (
        <button
          key={btn}
          onClick={() => {
            if (btn === 'DEL') onDelete();
            else if (btn === 'OK') onSubmit();
            else onNumber(btn);
          }}
          disabled={disabled}
          className={`w-16 h-16 text-xl font-bold rounded border-2 border-gray-400 
            ${btn === 'OK' ? 'bg-green-500 hover:bg-green-600 text-white border-green-600' : 
              btn === 'DEL' ? 'bg-red-500 hover:bg-red-600 text-white border-red-600' : 
              'bg-white hover:bg-gray-100 text-gray-900'}
            disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
        >
          {btn}
        </button>
      ))}
    </div>
  );
};

// Round sequence structure - matches the table exactly
// Step 1-2: 4 pairs, no questions
// Step 3-7: 2 pairs, 2 questions
// Step 8-9: 1 pair, 2 questions
// Step 10-12: 0 pairs, 2 questions
const ROUND_SEQUENCE = [
  { pairsToShow: 4, questionsToAsk: 0 },   // Step 1
  { pairsToShow: 4, questionsToAsk: 0 },   // Step 2
  { pairsToShow: 2, questionsToAsk: 2 },   // Step 3
  { pairsToShow: 2, questionsToAsk: 2 },   // Step 4
  { pairsToShow: 2, questionsToAsk: 2 },   // Step 5
  { pairsToShow: 2, questionsToAsk: 2 },   // Step 6
  { pairsToShow: 2, questionsToAsk: 2 },   // Step 7
  { pairsToShow: 1, questionsToAsk: 2 },   // Step 8
  { pairsToShow: 1, questionsToAsk: 2 },   // Step 9
  { pairsToShow: 0, questionsToAsk: 2 },   // Step 10 - NO pairs, just questions
  { pairsToShow: 0, questionsToAsk: 2 },   // Step 11
  { pairsToShow: 0, questionsToAsk: 2 }    // Step 12
];

const MEKGame = () => {
  // Debug overlays reveal answers; only staff may toggle them (Shift+O)
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  
  // Get level settings from navigation state
  const { 
    subject, 
    level = 3, 
    rounds: totalRounds = 2, 
    questionTime = 7
  } = location.state || {};

  // Game state
  // Phones stack the pairs above the question and use smaller tiles
  const [isPhone, setIsPhone] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [gamePhase, setGamePhase] = useState('loading'); // loading, memorize, memorize-question, question, rest, results
  const [currentRound, setCurrentRound] = useState(1);
  const [roundPairs, setRoundPairs] = useState([]);
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [pairsShownSoFar, setPairsShownSoFar] = useState(0);
  const [questionsAskedSoFar, setQuestionsAskedSoFar] = useState(0);
  const [currentPairsToDisplay, setCurrentPairsToDisplay] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [timer, setTimer] = useState(0);
  const [questionTimer, setQuestionTimer] = useState(0); // Separate timer for questions
  const [answer, setAnswer] = useState('');
  const [answerSaved, setAnswerSaved] = useState(false);
  const [currentQuestions, setCurrentQuestions] = useState([]); // Questions for current step
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  
  // Results tracking - use refs to avoid timer/state race conditions
  const round1AnswersRef = useRef(new Map()); // pairId -> { pairId, correctNumber, userAnswer, isCorrect, submitted }
  const round2AnswersRef = useRef(new Map());
  const [resultsReady, setResultsReady] = useState(false); // trigger re-render for results
  const [questionsForRound, setQuestionsForRound] = useState([]);
  
  // Exit modal
  const [showExitModal, setShowExitModal] = useState(false);
  
  // Debug mode
  const [showDebug, setShowDebug] = useState(false);
  
  // Refs
  const timerRef = useRef(null);
  const questionTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const questionsRef = useRef([]);
  const questionIdxRef = useRef(0);
  const seqDataRef = useRef({});
  const answerRef = useRef('');
  const currentRoundRef = useRef(1);
  const answerSavedRef = useRef(false); // Track if current question's answer was already submitted
  const askedPairIdsRef = useRef(new Set()); // Track which pairs have been asked as questions
  const shownPairIdsRef = useRef(new Set()); // Track which pairs have been displayed to the user
  const displayedPairIdsRef = useRef(new Set()); // Track IDs of pairs CURRENTLY visible on screen

  // Initialize game
  useEffect(() => {
    startTimeRef.current = Date.now();
    initializeRound(1);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    };
  }, []);

  // Debug mode toggle (Shift+O)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isStaff && e.shiftKey && e.key === 'O') {
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isStaff]);

  const initializeRound = (roundNum) => {
    const pairs = generateRoundPairs();
    setRoundPairs(pairs);
    setCurrentRound(roundNum);
    currentRoundRef.current = roundNum;
    setSequenceIndex(0);
    setPairsShownSoFar(0);
    setQuestionsAskedSoFar(0);
    setQuestionsForRound([]);
    
    shownPairIdsRef.current = new Set();
    askedPairIdsRef.current = new Set();
    displayedPairIdsRef.current = new Set();
    
    // Pre-allocate questions for each step at round start
    // Steps 1-2: no questions. Steps 3-12: 2 questions each = 20 total.
    // Questions are drawn from pairs shown in PREVIOUS steps, not the current step's pairs.
    const stepQuestions = [];
    const shownSoFar = new Set();
    const askedSoFar = new Set();
    let pairIdx = 0;
    
    for (let i = 0; i < ROUND_SEQUENCE.length; i++) {
      const step = ROUND_SEQUENCE[i];
      const currentStepPairIds = new Set();
      
      // Track which pairs this step will display
      for (let j = 0; j < step.pairsToShow && pairIdx < pairs.length; j++) {
        currentStepPairIds.add(pairs[pairIdx].id);
        pairIdx++;
      }
      
      // Pick questions from previously shown pairs (not current step's pairs, not already asked)
      const eligible = pairs.filter(p => shownSoFar.has(p.id) && !askedSoFar.has(p.id) && !currentStepPairIds.has(p.id));
      const shuffled = [...eligible].sort(() => Math.random() - 0.5);
      const questions = shuffled.slice(0, step.questionsToAsk);
      questions.forEach(q => askedSoFar.add(q.id));
      
      // After picking questions, mark current step's pairs as shown for future steps
      currentStepPairIds.forEach(id => shownSoFar.add(id));
      
      stepQuestions.push(questions);
    }
    
    startSequenceStep(0, pairs, 0, 0, stepQuestions);
  };

  const startSequenceStep = (seqIdx, pairs, pairsShown, questionsAsked, stepQuestions) => {
    if (seqIdx >= ROUND_SEQUENCE.length) {
      // Round complete - use ref for accurate round number
      if (currentRoundRef.current < totalRounds) {
        setGamePhase('rest');
        setTimer(15);
        startRestTimer(stepQuestions);
      } else {
        setGamePhase('results');
      }
      return;
    }

    const step = ROUND_SEQUENCE[seqIdx];
    const questions = stepQuestions[seqIdx] || [];
    const newPairsShown = pairsShown + step.pairsToShow;
    
    if (step.pairsToShow > 0 && step.questionsToAsk > 0) {
      // Steps 3-9: Show pairs AND questions simultaneously
      const pairsToDisplay = pairs.slice(pairsShown, pairsShown + step.pairsToShow);
      setCurrentPairsToDisplay(pairsToDisplay);
      displayedPairIdsRef.current = new Set(pairsToDisplay.map(p => p.id));
      
      questionsRef.current = questions;
      questionIdxRef.current = 0;
      seqDataRef.current = { seqIdx, pairs, pairsShown: newPairsShown, questionsAsked: questionsAsked + questions.length };
      
      setCurrentQuestions(questions);
      setCurrentQuestionIdx(0);
      setCurrentQuestion(questions[0] || null);
      setAnswer('');
      answerRef.current = '';
      setAnswerSaved(false);
      answerSavedRef.current = false;
      setGamePhase('memorize-question');
      setTimer(30);
      setQuestionTimer(questionTime);
      
      startSimultaneousTimers(seqIdx, pairs, newPairsShown, questionsAsked + questions.length, questions, stepQuestions);
    } else if (step.pairsToShow > 0) {
      // Steps 1-2: Only show pairs for memorization (no questions)
      const pairsToDisplay = pairs.slice(pairsShown, pairsShown + step.pairsToShow);
      setCurrentPairsToDisplay(pairsToDisplay);
      displayedPairIdsRef.current = new Set(pairsToDisplay.map(p => p.id));
      setGamePhase('memorize');
      setTimer(30);
      startMemorizeTimer(seqIdx, pairs, newPairsShown, questionsAsked, stepQuestions);
    } else if (step.questionsToAsk > 0) {
      // Steps 10-12: Only questions, no new pairs
      displayedPairIdsRef.current = new Set();
      setCurrentPairsToDisplay([]);
      startQuestionsFromPreallocated(seqIdx, pairs, pairsShown, questionsAsked, questions, stepQuestions);
    }
  };

  const startMemorizeTimer = (seqIdx, pairs, newPairsShown, questionsAsked, stepQuestions) => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setSequenceIndex(seqIdx + 1);
          setPairsShownSoFar(newPairsShown);
          startSequenceStep(seqIdx + 1, pairs, newPairsShown, questionsAsked, stepQuestions);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startSimultaneousTimers = (seqIdx, pairs, newPairsShown, totalQuestionsAsked, questions, stepQuestions) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (questionTimerRef.current) clearInterval(questionTimerRef.current);
    
    const totalQuestions = questions.length;
    const questionState = { idx: 0, secondsLeft: questionTime };
    const stepState = { active: true };
    
    // Main 30s timer for pairs
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Save pending answer before moving on
          if (questionTimerRef.current) {
            const currentQ = questions[questionState.idx];
            if (currentQ && !answerSavedRef.current) {
              saveAnswer(currentQ, answerRef.current);
            }
            clearInterval(questionTimerRef.current);
          }
          stepState.active = false;
          answerSavedRef.current = false;
          setSequenceIndex(seqIdx + 1);
          setPairsShownSoFar(newPairsShown);
          setQuestionsAskedSoFar(totalQuestionsAsked);
          startSequenceStep(seqIdx + 1, pairs, newPairsShown, totalQuestionsAsked, stepQuestions);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    if (totalQuestions === 0) return;
    
    questionTimerRef.current = setInterval(() => {
      if (!stepState.active) {
        clearInterval(questionTimerRef.current);
        return;
      }
      
      questionState.secondsLeft--;
      setQuestionTimer(questionState.secondsLeft);
      
      if (questionState.secondsLeft <= 0) {
        const currentQ = questions[questionState.idx];
        if (currentQ && !answerSavedRef.current) {
          saveAnswer(currentQ, answerRef.current);
        }
        
        answerSavedRef.current = false;
        questionState.idx++;
        if (questionState.idx < totalQuestions) {
          setCurrentQuestionIdx(questionState.idx);
          setCurrentQuestion(questions[questionState.idx]);
          setAnswer('');
          answerRef.current = '';
          setAnswerSaved(false);
          answerSavedRef.current = false;
          questionState.secondsLeft = questionTime;
          setQuestionTimer(questionTime);
        } else {
          clearInterval(questionTimerRef.current);
          setCurrentQuestion(null);
        }
      }
    }, 1000);
  };

  const startQuestionsFromPreallocated = (seqIdx, pairs, pairsShown, questionsAsked, questions, stepQuestions) => {
    setQuestionsForRound(prev => [...prev, ...questions]);
    askQuestion(0, questions, seqIdx, pairs, pairsShown, questionsAsked + questions.length, stepQuestions);
  };

  const askQuestion = (qIdx, questions, seqIdx, pairs, pairsShown, totalQuestionsAsked, stepQuestions) => {
    if (qIdx >= questions.length) {
      setSequenceIndex(seqIdx + 1);
      setPairsShownSoFar(pairsShown);
      setQuestionsAskedSoFar(totalQuestionsAsked);
      startSequenceStep(seqIdx + 1, pairs, pairsShown, totalQuestionsAsked, stepQuestions);
      return;
    }

    setCurrentQuestion(questions[qIdx]);
    setQuestionIndex(qIdx);
    setAnswer('');
    answerRef.current = '';
    setAnswerSaved(false);
    answerSavedRef.current = false;
    setGamePhase('question');
    setTimer(questionTime);
    
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!answerSavedRef.current) {
            saveAnswer(questions[qIdx], answerRef.current);
          }
          answerSavedRef.current = false;
          setTimeout(() => {
            askQuestion(qIdx + 1, questions, seqIdx, pairs, pairsShown, totalQuestionsAsked, stepQuestions);
          }, 100);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const saveAnswer = useCallback((question, userAnswer, fromSubmit = false) => {
    const map = currentRoundRef.current === 1 ? round1AnswersRef.current : round2AnswersRef.current;
    
    // Never overwrite an answer that was explicitly submitted by the user
    const existing = map.get(question.id);
    if (existing && existing.submitted && !fromSubmit) {
      return;
    }
    
    const isCorrect = parseInt(userAnswer) === question.number;
    map.set(question.id, {
      pairId: question.id,
      correctNumber: question.number,
      userAnswer: userAnswer || '',
      isCorrect,
      submitted: fromSubmit
    });
  }, []);

  const startRestTimer = (stepQuestions) => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          initializeRound(2);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleNumber = (num) => {
    if (answer.length < 2) {
      const newAnswer = answer + num;
      setAnswer(newAnswer);
      answerRef.current = newAnswer;
    }
  };

  const handleDelete = () => {
    setAnswer('');
    answerRef.current = '';
  };

  const handleSubmit = () => {
    if (answer.length > 0 && currentQuestion) {
      saveAnswer(currentQuestion, answer, true);
      setAnswerSaved(true);
      answerSavedRef.current = true;
    }
  };

  const handleExit = () => {
    setShowExitModal(true);
  };

  const confirmExit = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    navigate(`${gamesBase}/mek/${subjectId}`);
  };

  const handleSaveResults = async () => {
    const round1Correct = round1Answers.filter(a => a.isCorrect).length;
    const round2Correct = round2Answers.filter(a => a.isCorrect).length;
    
    try {
      await apiClient.post(`/api/courses/mek-game/runs/`, {
        subject: subjectId,
        round1_correct: round1Correct,
        round1_total: round1Answers.length,
        round2_correct: round2Correct,
        round2_total: round2Answers.length,
        level: level,
        max_score: 100,
        rounds_data: {
          round1: round1Answers,
          round2: round2Answers,
          level: level
        },
        started_at: new Date(startTimeRef.current).toISOString(),
      });
      navigate(`${gamesBase}/mek/${subjectId}`);
    } catch (error) {
      console.error('Failed to save results:', error);
      navigate(`${gamesBase}/mek/${subjectId}`);
    }
  };

  // Build answer arrays from refs
  const round1Answers = Array.from(round1AnswersRef.current.values());
  const round2Answers = Array.from(round2AnswersRef.current.values());
  
  // Calculate results
  const round1Correct = round1Answers.filter(a => a.isCorrect).length;
  const round2Correct = round2Answers.filter(a => a.isCorrect).length;
  const totalCorrect = round1Correct + round2Correct;
  const totalQuestions = round1Answers.length + round2Answers.length;
  const round1Percentage = round1Answers.length > 0 ? (round1Correct / round1Answers.length * 100).toFixed(1) : 0;
  const round2Percentage = round2Answers.length > 0 ? (round2Correct / round2Answers.length * 100).toFixed(1) : 0;
  const finalPercentage = totalQuestions > 0 ? (totalCorrect / totalQuestions * 100).toFixed(1) : 0;

  return (
    <div className="fixed inset-0 top-16 bg-white flex flex-col overflow-hidden" style={{ animation: 'fadeFromBlack 1.5s ease-out' }}>
      <style>{`
        @keyframes fadeFromBlack {
          0% { filter: brightness(0); }
          100% { filter: brightness(1); }
        }
      `}</style>
      {/* Exit button */}
      <button
        onClick={handleExit}
        className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg z-50"
      >
        <XMarkIcon className="w-6 h-6 text-white" />
      </button>

      {/* Loading Phase */}
      {gamePhase === 'loading' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      )}

      {/* Memorize Phase (Steps 1-2: pairs only, no questions) */}
      {gamePhase === 'memorize' && (
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
          {/* Left side - Pairs */}
          <div className="w-full md:w-1/2 flex flex-col items-center md:items-start justify-start pt-16 md:pt-6 px-4 md:px-0 md:pl-32">
            {showDebug && (
              <div className="text-base font-medium text-gray-700 mb-4">
                Round {currentRound} Step {sequenceIndex + 1} - Memorize these pairs ({timer}s)
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-2 md:gap-3">
              {currentPairsToDisplay.map((pair) => (
                <PairDisplay key={pair.id} pair={pair} size={isPhone ? 64 : 130} />
              ))}
            </div>
          </div>
          
          {/* Right side - Empty during memorize (no separator or text) */}
          <div className="flex-1" />
        </div>
      )}

      {/* Memorize-Question Phase (Steps 3-10: pairs on left with 30s timer, questions on right with 7s timer) */}
      {gamePhase === 'memorize-question' && (
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto">
          {/* Left side - Pairs with 30s timer */}
          <div className="w-full md:w-1/2 flex flex-col items-center md:items-start justify-start pt-16 md:pt-6 px-4 md:px-0 md:pl-32">
            {showDebug && (
              <div className="text-base font-medium text-gray-700 mb-4">
                Round {currentRound} Step {sequenceIndex + 1} - Memorize these pairs ({timer}s)
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-2 md:gap-3">
              {currentPairsToDisplay.map((pair) => (
                <PairDisplay key={pair.id} pair={pair} size={isPhone ? 64 : 130} />
              ))}
            </div>
          </div>
          
          {/* Right side - Question with 7s timer */}
          <div className="w-full md:w-1/2 flex flex-col items-center justify-start pt-4 pb-6 md:pt-6 md:pr-16">
            {currentQuestion && !displayedPairIdsRef.current.has(currentQuestion.id) ? (
              <>
                {showDebug && (
                  <div className="text-base font-medium text-gray-700 mb-4">
                    Question ({questionTimer}s)
                  </div>
                )}
                
                {/* Question image and answer input */}
                <div className="flex items-center gap-4 mb-4">
                  <ShapeRenderer image={currentQuestion.image} size={isPhone ? 88 : 120} />
                  <div 
                    className="flex items-center justify-center font-bold text-4xl text-gray-900 border-2 border-gray-400 rounded bg-white"
                    style={{ width: isPhone ? 88 : 120, height: isPhone ? 88 : 120 }}
                  >
                    {answer || '__'}
                  </div>
                </div>
                
                {answerSaved && (
                  <div className="text-green-600 font-bold mb-2">Answer saved!</div>
                )}
                
                {/* Numpad */}
                <Numpad 
                  onNumber={handleNumber}
                  onDelete={handleDelete}
                  onSubmit={handleSubmit}
                  disabled={questionTimer === 0}
                />
                
                {/* Debug: Show correct answer (Shift+O to toggle) */}
                {showDebug && currentQuestion && (
                  <div className="mt-2 text-xs text-red-500 font-mono">
                    DEBUG: Correct answer = {currentQuestion.number}
                  </div>
                )}
              </>
            ) : (
              showDebug && <div className="text-gray-500">Questions completed for this step</div>
            )}
          </div>
        </div>
      )}

      {/* Question Phase (Steps 11-12: questions only, no new pairs) */}
      {gamePhase === 'question' && currentQuestion && (
        <div className="flex-1 flex overflow-y-auto">
          {/* Left side - Empty (no new pairs for steps 11-12) */}
          <div className="hidden md:flex w-1/2 flex-col items-start justify-start pt-6 pl-12">
            {showDebug && (
              <div className="text-base font-medium text-gray-700 mb-4">
                Round {currentRound} Step {sequenceIndex + 1}
              </div>
            )}
          </div>
          
          {/* Right side - Question */}
          <div className="w-full md:w-96 flex flex-col items-center justify-start pt-16 md:pt-6 p-4 md:p-8">
            {showDebug && (
              <div className="text-base font-medium text-gray-700 mb-4">
                Question ({timer}s)
              </div>
            )}
            
            {/* Question image and answer input */}
            <div className="flex items-center gap-4 mb-4">
              <ShapeRenderer image={currentQuestion.image} size={isPhone ? 88 : 120} />
              <div 
                className="flex items-center justify-center font-bold text-4xl text-gray-900 border-2 border-gray-400 rounded bg-white"
                style={{ width: isPhone ? 88 : 120, height: isPhone ? 88 : 120 }}
              >
                {answer || '__'}
              </div>
            </div>
            
            {answerSaved && (
              <div className="text-green-600 font-bold mb-2">Answer saved!</div>
            )}
            
            {/* Numpad */}
            <Numpad 
              onNumber={handleNumber}
              onDelete={handleDelete}
              onSubmit={handleSubmit}
              disabled={timer === 0}
            />
            
            {/* Debug: Show correct answer (Shift+O to toggle) */}
            {showDebug && currentQuestion && (
              <div className="mt-2 text-xs text-red-500 font-mono">
                DEBUG: Correct answer = {currentQuestion.number}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rest Phase */}
      {gamePhase === 'rest' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Round 1 Complete!</h2>
            <p className="text-gray-600 mb-2">Take a short break</p>
            <p className="text-4xl font-bold text-primary-600">{timer}s</p>
            <p className="text-gray-500 mt-4">Round 2 will start automatically</p>
          </div>
        </div>
      )}

      {/* Results Phase */}
      {gamePhase === 'results' && (
        <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto py-6 px-4">
          <div className="text-center space-y-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-800">Test Complete!</h2>
            
            {/* Level indicator */}
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              level === 1 ? 'bg-green-100 text-green-700' :
              level === 2 ? 'bg-yellow-100 text-yellow-700' :
              'bg-rose-100 text-rose-700'
            }`}>
              Level {level} {level === 3 ? '(Real Test)' : level === 2 ? '(Training)' : '(Practice)'}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-sm text-gray-600 mb-2">Final Score</p>
              <p className="text-5xl font-bold text-green-600">{finalPercentage}%</p>
              <p className="text-gray-500 mt-2">{totalCorrect} / {totalQuestions} correct</p>
            </div>
            
            {totalRounds > 1 ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Round 1</p>
                  <p className="text-2xl font-bold text-gray-800">{round1Percentage}%</p>
                  <p className="text-xs text-gray-500">{round1Correct} / {round1Answers.length}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Round 2</p>
                  <p className="text-2xl font-bold text-gray-800">{round2Percentage}%</p>
                  <p className="text-xs text-gray-500">{round2Correct} / {round2Answers.length}</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Round 1</p>
                <p className="text-2xl font-bold text-gray-800">{round1Percentage}%</p>
                <p className="text-xs text-gray-500">{round1Correct} / {round1Answers.length}</p>
              </div>
            )}
            
            {/* DEBUG: Answer dump - remove after fixing */}
            <details className="text-left text-xs bg-gray-100 rounded p-2 mt-2">
              <summary className="cursor-pointer font-bold text-red-600">Debug: View All Answers</summary>
              <div className="mt-2 max-h-40 overflow-y-auto">
                <p className="font-bold">Round 1 ({round1Answers.length} answers, {round1Correct} correct):</p>
                {round1Answers.map((a, i) => (
                  <div key={i} className={a.isCorrect ? 'text-green-700' : 'text-red-700'}>
                    Q{i+1}: pairId={a.pairId}, answer="{a.userAnswer}", correct={a.correctNumber}, {a.isCorrect ? '✓' : '✗'}
                  </div>
                ))}
                <p className="font-bold mt-2">Round 2 ({round2Answers.length} answers, {round2Correct} correct):</p>
                {round2Answers.map((a, i) => (
                  <div key={i} className={a.isCorrect ? 'text-green-700' : 'text-red-700'}>
                    Q{i+1}: pairId={a.pairId}, answer="{a.userAnswer}", correct={a.correctNumber}, {a.isCorrect ? '✓' : '✗'}
                  </div>
                ))}
              </div>
            </details>

            <button
              onClick={handleSaveResults}
              className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
            >
              Save & Exit
            </button>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Game?</h3>
            <p className="text-gray-600 mb-4">Your progress will not be saved.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitModal(false)}
                className="flex-1 py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MEKGame;
